import { useCallback, useState } from "react";
import type {
  DragEventHandler,
  KeyboardEventHandler,
  DragEvent,
  KeyboardEvent,
} from "react";

// firefox refuses to start a drag unless the payload is set, even when the
// key is read back from state rather than the drag event.
const DRAG_PAYLOAD_TYPE = "text/plain";

interface UseDragReorderOptions {
  /** item keys in their current display order */
  keys: string[];
  /** moves the item at `fromKey` into the position currently held by `toKey` */
  onReorder: (fromKey: string, toKey: string) => void;
  isDisabled?: boolean;
}

export interface DragHandleProps {
  draggable: boolean;
  onDragStart: DragEventHandler;
  onDragEnd: DragEventHandler;
  onKeyDown: KeyboardEventHandler;
}

export interface DropTargetProps {
  onDragOver: DragEventHandler;
  onDrop: DragEventHandler;
}

interface UseDragReorderResult {
  draggedKey: string | null;
  getDragHandleProps: (key: string) => DragHandleProps;
  getDropTargetProps: (key: string) => DropTargetProps;
}

/**
 * pointer and keyboard reordering for a list of keyed items. spread
 * `getDragHandleProps` on the grab handle and `getDropTargetProps` on the item
 * it moves, then style the in-flight item with `draggedKey === key`.
 */
export function useDragReorder({
  keys,
  onReorder,
  isDisabled = false,
}: UseDragReorderOptions): UseDragReorderResult {
  const [draggedKey, setDraggedKey] = useState<string | null>(null);

  const moveBy = useCallback(
    (key: string, offset: number) => {
      const index = keys.indexOf(key);
      if (index === -1) return;

      const targetKey = keys[index + offset];
      if (targetKey === undefined) return;

      onReorder(key, targetKey);
    },
    [keys, onReorder],
  );

  const getDragHandleProps = useCallback(
    (key: string): DragHandleProps => ({
      draggable: !isDisabled,
      onDragStart: (event: DragEvent) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(DRAG_PAYLOAD_TYPE, key);
        setDraggedKey(key);
      },
      onDragEnd: () => setDraggedKey(null),
      onKeyDown: (event: KeyboardEvent) => {
        if (isDisabled) return;
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

        event.preventDefault();
        moveBy(key, event.key === "ArrowUp" ? -1 : 1);
      },
    }),
    [isDisabled, moveBy],
  );

  const getDropTargetProps = useCallback(
    (key: string): DropTargetProps => ({
      onDragOver: (event: DragEvent) => event.preventDefault(),
      onDrop: (event: DragEvent) => {
        event.preventDefault();
        setDraggedKey(null);

        if (isDisabled || draggedKey === null || draggedKey === key) return;
        // the list can change while a drag is in flight; a key that is gone
        // resolves to -1 downstream and reorders items nobody dragged.
        if (!keys.includes(draggedKey) || !keys.includes(key)) return;

        onReorder(draggedKey, key);
      },
    }),
    [draggedKey, isDisabled, keys, onReorder],
  );

  return { draggedKey, getDragHandleProps, getDropTargetProps };
}
